import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-white text-gray-800" }
const _hoisted_2 = { class: "sr-only-focusable" }
const _hoisted_3 = {
  class: "bg-gray-50 border-b border-gray-200 py-4",
  "aria-label": "Breadcrumb navigation"
}
const _hoisted_4 = {
  class: "flex items-center max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 list-none",
  role: "list"
}
const _hoisted_5 = { class: "flex items-center" }
const _hoisted_6 = {
  class: "flex items-center text-gray-500 font-medium ml-2",
  "aria-current": "page"
}
const _hoisted_7 = { class: "text-gray-800" }
const _hoisted_8 = { class: "flex max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12 gap-6 lg:gap-8" }
const _hoisted_9 = { class: "flex justify-between items-center mb-4" }
const _hoisted_10 = ["aria-expanded", "aria-label"]
const _hoisted_11 = { class: "sr-only" }
const _hoisted_12 = {
  class: "space-y-1",
  role: "list"
}
const _hoisted_13 = ["href", "onClick", "onKeydown", "aria-label"]
const _hoisted_14 = { class: "mb-8 pb-6 border-b-2 border-gray-200" }
const _hoisted_15 = {
  id: "page-title",
  class: "text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-2 leading-tight",
  tabindex: "-1"
}
const _hoisted_16 = {
  key: 0,
  class: "text-gray-500 text-sm italic"
}
const _hoisted_17 = ["datetime"]

import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import type { TocItem } from '@/types/legal'
import { addBreadcrumbStructuredData, removeBreadcrumbStructuredData } from '@/utils/sitemapUtils'

interface Props {
  title: string
  lastUpdated?: string
  showTableOfContents?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LegalPageLayout',
  props: {
    title: {},
    lastUpdated: {},
    showTableOfContents: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props

const contentRef = ref<HTMLElement>()
const tocItems = ref<TocItem[]>([])
const isTocCollapsed = ref(true) // Start collapsed on mobile

const generateTableOfContents = () => {
  if (!contentRef.value || !props.showTableOfContents) return

  const headings = contentRef.value.querySelectorAll('h2, h3, h4')
  const items: TocItem[] = []

  headings.forEach((heading, index) => {
    const level = parseInt(heading.tagName.charAt(1)) - 1 // h2=1, h3=2, h4=3
    let id = heading.id

    // Generate ID if not present
    if (!id) {
      id = `section-${index + 1}`
      heading.id = id
    }

    items.push({
      id,
      text: heading.textContent || '',
      level
    })
  })

  tocItems.value = items
}

const scrollToSection = (id: string, event: Event) => {
  event.preventDefault()
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    })
    // Close TOC on mobile after navigation
    if (window.innerWidth < 768) {
      isTocCollapsed.value = true
    }
  }
}

const toggleToc = () => {
  isTocCollapsed.value = !isTocCollapsed.value
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Accessibility functions for skip links
const skipToMainContent = (event: Event) => {
  event.preventDefault()
  const mainContent = document.getElementById('main-content')
  if (mainContent) {
    mainContent.focus()
    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const skipToTableOfContents = (event: Event) => {
  event.preventDefault()
  const toc = document.getElementById('table-of-contents')
  if (toc) {
    toc.focus()
    toc.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// Keyboard navigation for table of contents
const handleTocKeydown = (event: KeyboardEvent, currentIndex: number) => {
  const tocLinks = document.querySelectorAll('#toc-list a')
  
  switch (event.key) {
    case 'ArrowDown': {
      event.preventDefault()
      const nextIndex = currentIndex + 1 < tocLinks.length ? currentIndex + 1 : 0
      ;(tocLinks[nextIndex] as HTMLElement).focus()
      break
    }
      
    case 'ArrowUp': {
      event.preventDefault()
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : tocLinks.length - 1
      ;(tocLinks[prevIndex] as HTMLElement).focus()
      break
    }
      
    case 'Home':
      event.preventDefault()
      ;(tocLinks[0] as HTMLElement).focus()
      break
      
    case 'End':
      event.preventDefault()
      ;(tocLinks[tocLinks.length - 1] as HTMLElement).focus()
      break
      
    case 'Enter':
    case ' ':
      event.preventDefault()
      ;(tocLinks[currentIndex] as HTMLElement).click()
      break
  }
}

// Handle window resize
const handleResize = () => {
  if (window.innerWidth >= 768) {
    isTocCollapsed.value = false
  } else {
    isTocCollapsed.value = true
  }
}

onMounted(async () => {
  await nextTick()
  generateTableOfContents()
  
  // Set initial TOC state based on screen size
  handleResize()
  window.addEventListener('resize', handleResize)
  
  // Add breadcrumb structured data
  const breadcrumbs = [
    { name: 'Home', url: window.location.origin },
    { name: props.title, url: window.location.href }
  ]
  addBreadcrumbStructuredData(breadcrumbs)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  removeBreadcrumbStructuredData()
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("a", {
        href: "#main-content",
        class: "skip-link bg-blue-600 text-white px-4 py-2 rounded-md font-medium focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        onClick: skipToMainContent
      }, " Skip to main content "),
      (_ctx.showTableOfContents && tocItems.value.length > 0)
        ? (_openBlock(), _createElementBlock("a", {
            key: 0,
            href: "#table-of-contents",
            class: "skip-link bg-blue-600 text-white px-4 py-2 rounded-md font-medium focus:not-sr-only focus:absolute focus:top-4 focus:left-32 focus:z-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ml-2",
            onClick: skipToTableOfContents
          }, " Skip to table of contents "))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("nav", _hoisted_3, [
      _createElementVNode("ol", _hoisted_4, [
        _createElementVNode("li", _hoisted_5, [
          _createVNode(_component_router_link, {
            to: "/",
            class: "text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:rounded-sm",
            "aria-label": "Navigate to home page"
          }, {
            default: _withCtx(() => _cache[0] || (_cache[0] = [
              _createTextVNode(" Home ", -1)
            ])),
            _: 1,
            __: [0]
          })
        ]),
        _createElementVNode("li", _hoisted_6, [
          _cache[1] || (_cache[1] = _createElementVNode("span", {
            class: "mr-2",
            "aria-hidden": "true"
          }, "/", -1)),
          _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.title), 1)
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_8, [
      (_ctx.showTableOfContents && tocItems.value.length > 0)
        ? (_openBlock(), _createElementBlock("aside", {
            key: 0,
            id: "table-of-contents",
            class: _normalizeClass(["w-full lg:w-80 xl:w-96 bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 h-fit sticky top-6 order-first lg:order-none", { 'lg:block': !isTocCollapsed.value, 'lg:block': isTocCollapsed.value }]),
            role: "complementary",
            "aria-labelledby": "toc-heading"
          }, [
            _createElementVNode("div", _hoisted_9, [
              _cache[3] || (_cache[3] = _createElementVNode("h3", {
                id: "toc-heading",
                class: "text-lg font-semibold text-gray-800"
              }, "Table of Contents", -1)),
              _createElementVNode("button", {
                class: "lg:hidden p-2 rounded-md hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] min-w-[44px]",
                onClick: toggleToc,
                "aria-expanded": !isTocCollapsed.value,
                "aria-controls": "toc-list",
                "aria-label": `${isTocCollapsed.value ? 'Show' : 'Hide'} table of contents`,
                type: "button"
              }, [
                _createElementVNode("span", _hoisted_11, _toDisplayString(isTocCollapsed.value ? 'Show' : 'Hide') + " table of contents", 1),
                (_openBlock(), _createElementBlock("svg", {
                  class: _normalizeClass(["w-5 h-5 transition-transform duration-200", { 'rotate-180': !isTocCollapsed.value }]),
                  fill: "none",
                  stroke: "currentColor",
                  viewBox: "0 0 24 24",
                  "aria-hidden": "true"
                }, _cache[2] || (_cache[2] = [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M19 9l-7 7-7-7"
                  }, null, -1)
                ]), 2))
              ], 8, _hoisted_10)
            ]),
            _createElementVNode("nav", {
              id: "toc-list",
              class: _normalizeClass(["transition-all duration-300 ease-in-out", { 'hidden lg:block': isTocCollapsed.value, 'block': !isTocCollapsed.value }]),
              "aria-label": "Table of contents navigation",
              role: "navigation"
            }, [
              _createElementVNode("ul", _hoisted_12, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tocItems.value, (item, index) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: item.id,
                    class: _normalizeClass({
                'ml-0': item.level === 1,
                'ml-4': item.level === 2,
                'ml-8': item.level === 3
              }),
                    role: "listitem"
                  }, [
                    _createElementVNode("a", {
                      href: `#${item.id}`,
                      class: _normalizeClass(["block text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-md text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] flex items-center", {
                  'font-medium': item.level === 1,
                  'text-xs': item.level === 3
                }]),
                      onClick: ($event: any) => (scrollToSection(item.id, $event)),
                      onKeydown: ($event: any) => (handleTocKeydown($event, index)),
                      "aria-label": `Navigate to section: ${item.text}`,
                      role: "link"
                    }, _toDisplayString(item.text), 43, _hoisted_13)
                  ], 2))
                }), 128))
              ])
            ], 2)
          ], 2))
        : _createCommentVNode("", true),
      _createElementVNode("main", {
        id: "main-content",
        class: _normalizeClass(["flex-1 min-w-0", { 'max-w-4xl mx-auto': !_ctx.showTableOfContents || tocItems.value.length === 0 }]),
        role: "main",
        "aria-labelledby": 'page-title',
        tabindex: "-1"
      }, [
        _createElementVNode("header", _hoisted_14, [
          _createElementVNode("h1", _hoisted_15, _toDisplayString(_ctx.title), 1),
          (_ctx.lastUpdated)
            ? (_openBlock(), _createElementBlock("p", _hoisted_16, [
                _cache[4] || (_cache[4] = _createTextVNode(" Last updated: ", -1)),
                _createElementVNode("time", { datetime: _ctx.lastUpdated }, _toDisplayString(formatDate(_ctx.lastUpdated)), 9, _hoisted_17)
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", {
          class: "prose prose-gray max-w-none text-gray-700 leading-relaxed",
          ref_key: "contentRef",
          ref: contentRef,
          role: "document"
        }, [
          _renderSlot(_ctx.$slots, "default")
        ], 512)
      ], 2)
    ])
  ]))
}
}

})